import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import type {} from '@redux-devtools/extension'; // required for devtools typing

import { createTasksSlice, type TasksSlice } from './slices/tasksSlice';
import { createUiSlice, type UiSlice } from './slices/uiSlice';
import {
  createTaskFiltersSlice,
  type TaskFiltersSlice,
} from './slices/taskFiltersSlice';
import { createMapSlice, type MapSlice } from './slices/mapSlice';
import {
  createNavigationSlice,
  type NavigationSlice,
} from './slices/navigationSlice';
import { createUserSlice, type UserSlice } from './slices/userSlice';
import {
  createTaskNavigationSlice,
  type TaskNavigationSlice,
} from './slices/taskNavigationSlice';
import {
  createDrawingToolsSlice,
  type DrawingToolsSlice,
} from './slices/drawingToolsSlice';
import {
  createToolPreferencesSlice,
  type ToolPreferencesSlice,
} from './slices/toolPreferencesSlice';
import { createGuidesSlice, type GuidesSlice } from './slices/guidesSlice';
import { createSearchSlice, type SearchSlice } from './slices/searchSlice';
import { createAuthSlice, type AuthSlice } from './slices/authSlice';
import {
  createWorkspaceSlice,
  type WorkspaceSlice,
} from './slices/workspaceSlice';
import {
  createPresenceSlice,
  type PresenceSlice,
} from './slices/presenceSlice';
import { createNotesSlice, type NotesSlice } from './slices/notesSlice';
import {
  createNoteFiltersSlice,
  type NoteFiltersSlice,
} from './slices/noteFiltersSlice';
import {
  createMapLayersSlice,
  type MapLayersSlice,
} from './slices/mapLayersSlice';
import {
  createFormConflictSlice,
  type FormConflictSlice,
} from './slices/formConflictSlice';
import {
  createShareFormSlice,
  type ShareFormSlice,
} from './slices/shareFormSlice';
import {
  createObstructionsSlice,
  type ObstructionsSlice,
} from './slices/obstructionsSlice';
import {
  createObstructionFiltersSlice,
  type ObstructionFiltersSlice,
} from './slices/obstructionFiltersSlice';
import {
  createPublicShareSlice,
  type PublicShareSlice,
} from './slices/publicShareSlice';
import {
  createWorkspaceMembersSlice,
  type WorkspaceMembersSlice,
} from './slices/workspaceMembersSlice';

// ===== STORE TYPE =====

export type GeoMarkupStore = TasksSlice &
  UiSlice &
  TaskFiltersSlice &
  MapSlice &
  NavigationSlice &
  UserSlice &
  TaskNavigationSlice &
  DrawingToolsSlice &
  ToolPreferencesSlice &
  GuidesSlice &
  SearchSlice &
  AuthSlice &
  WorkspaceSlice &
  PresenceSlice &
  NotesSlice &
  NoteFiltersSlice &
  ObstructionsSlice &
  ObstructionFiltersSlice &
  MapLayersSlice &
  FormConflictSlice &
  ShareFormSlice &
  PublicShareSlice &
  WorkspaceMembersSlice;

// ===== STORE CREATION =====

export const useGeoMarkupStore = create<GeoMarkupStore>()(
  devtools(
    immer((...a) => ({
      ...createTasksSlice(...a),
      ...createUiSlice(...a),
      ...createTaskFiltersSlice(...a),
      ...createMapSlice(...a),
      ...createNavigationSlice(...a),
      ...createUserSlice(...a),
      ...createTaskNavigationSlice(...a),
      ...createDrawingToolsSlice(...a),
      ...createToolPreferencesSlice(...a),
      ...createGuidesSlice(...a),
      ...createSearchSlice(...a),
      ...createAuthSlice(...a),
      ...createWorkspaceSlice(...a),
      ...createPresenceSlice(...a),
      ...createNotesSlice(...a),
      ...createNoteFiltersSlice(...a),
      ...createObstructionsSlice(...a),
      ...createObstructionFiltersSlice(...a),
      ...createMapLayersSlice(...a),
      ...createFormConflictSlice(...a),
      ...createShareFormSlice(...a),
      ...createPublicShareSlice(...a),
      ...createWorkspaceMembersSlice(...a),
    })),
    {
      name: 'GeoMarkupStore',
      enabled: import.meta.env.DEV,
    }
  )
);

// ===== SELECTORS =====

export const selectTasks = (state: GeoMarkupStore) => state.tasks;
export const selectTaskFilters = (state: GeoMarkupStore) => state.taskFilters;
export const selectUi = (state: GeoMarkupStore) => state.ui;
export const selectMap = (state: GeoMarkupStore) => state.map;
export const selectNavigation = (state: GeoMarkupStore) => state.navigation;
export const selectUser = (state: GeoMarkupStore) => state.user;

// Re-export advanced selectors from dedicated selector files
export {
  selectAllTasks,
  selectUniqueLocations,
  selectSelectedTask,
} from './selectors/taskSelectors';
